'use client'

/**
 * Botão da página de informações da pós que abre o chat comercial no
 * lugar dos antigos formulários modais ("Tenho uma Dúvida", "Ver Programa
 * do Completo", "Falar com Consultor").
 *
 * Envolve o `<Button>` existente via `Slot` (mesmo mecanismo do
 * `DialogTrigger asChild`), então o layout não muda. Fora das rotas ou
 * plataformas com chat configurado renderiza `null` — não há fallback
 * para o formulário.
 */

import { Slot } from '@radix-ui/react-slot'
import { usePathname } from 'next/navigation'
import type { ReactElement } from 'react'
import type { AgendorContactOrigin } from '@/helpers/postgraduate-agendor-funnel'
import { dispatchPostgraduateChatOpen } from '@/hooks/use-postgraduate-chat'
import { useUnity } from '@/hooks/use-unity'
import { getPostgraduateChatConfig } from '@/lib/postgraduate-chat/config'
import { isPostgraduateChatRoute } from '@/lib/postgraduate-chat/route'

interface PostgraduateChatTriggerProps {
  /** Botão clicado — vira `origem_contato` no Agendor (opção real do CRM). */
  readonly source: AgendorContactOrigin
  /** Um único elemento clicável; recebe o `onClick` pelo Slot. */
  readonly children: ReactElement
}

export function PostgraduateChatTrigger({
  source,
  children,
}: PostgraduateChatTriggerProps) {
  const pathname = usePathname()
  const { unity } = useUnity()

  const isChatAvailable =
    isPostgraduateChatRoute(pathname) &&
    Boolean(getPostgraduateChatConfig(unity?.reference))

  if (!isChatAvailable) {
    return null
  }

  return (
    <Slot onClick={() => dispatchPostgraduateChatOpen(source)}>{children}</Slot>
  )
}
